/**
 * Note snapshot shape (v1).
 *
 * Notes share the same `board` table as tactics/whiteboard with
 * `kind='note'`. We persist the editor's native document JSON (TipTap /
 * ProseMirror doc) verbatim so opening + saving is lossless. A short
 * plain-text preview is captured at save time for the overview-grid card.
 *
 * If we later add a Markdown export pipeline, it operates on `doc` →
 * Markdown one-way; the source of truth stays the doc JSON.
 */
export interface NoteSnapshot {
  /** Schema version — bump only when breaking the doc shape. */
  v: 1;
  /**
   * TipTap document JSON. Opaque to the rest of the app; only the editor
   * reads it. Stored as `unknown` to avoid pulling TipTap types into every
   * consumer (repos, list, sync). Validated by the editor at load time.
   */
  doc: unknown;
  /**
   * Plain-text preview captured at save time (max ~280 chars). Powers the
   * overview-grid card without forcing a full render. Optional for legacy /
   * brand-new notes.
   */
  preview?: string | null;
}

/** Default snapshot for a fresh note — a single empty paragraph. */
export function emptyNoteSnapshot(): NoteSnapshot {
  return {
    v: 1,
    doc: {
      type: "doc",
      content: [{ type: "paragraph" }],
    },
    preview: null,
  };
}

/**
 * Parse a stored snapshot string into a `NoteSnapshot`. Always returns a
 * valid object; on any error or schema mismatch we fall back to an empty
 * snapshot so the editor never crashes on a corrupt row.
 */
export function parseNoteSnapshot(raw: string | null | undefined): NoteSnapshot {
  if (!raw) return emptyNoteSnapshot();
  try {
    const parsed = JSON.parse(raw) as Partial<NoteSnapshot>;
    if (!parsed || typeof parsed !== "object") return emptyNoteSnapshot();
    return {
      v: 1,
      doc: parsed.doc ?? emptyNoteSnapshot().doc,
      preview:
        typeof parsed.preview === "string" && parsed.preview.length > 0 ? parsed.preview : null,
    };
  } catch {
    return emptyNoteSnapshot();
  }
}
